import { useEffect } from "react";
import { useParams, useNavigate } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentDashboardsApi } from "../api/agentDashboards";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useConfirm } from "../context/ConfirmContext";
import { queryKeys } from "../lib/queryKeys";
import { DashboardWidgetCard } from "../components/charts/DashboardWidgetCard";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { BarChart3, Trash2 } from "lucide-react";
import type { AgentDashboardWidget } from "@ligament/shared";

export function AgentDashboardDetail() {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const {
    data: dashboard,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.agentDashboards.detail(dashboardId!),
    queryFn: () => agentDashboardsApi.get(dashboardId!),
    enabled: !!dashboardId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentDashboardsApi.remove(dashboardId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentDashboards.list(selectedPracticeId!),
      });
      navigate("/analytics", { replace: true });
    },
  });

  useEffect(() => {
    setBreadcrumbs([
      { label: "Analytics", href: "/analytics" },
      { label: dashboard?.title ?? "Dashboard" },
    ]);
  }, [setBreadcrumbs, dashboard]);

  if (isLoading) return <PageSkeleton variant="dashboard" />;
  if (error)
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : "Failed to load dashboard"}
      </p>
    );
  if (!dashboard)
    return <EmptyState icon={BarChart3} message="Dashboard not found." />;

  const widgets = dashboard.widgets ?? [];
  // Widget positions are always on a 12-column grid. dashboard.layout.columns is a
  // semantic hint (widgets-per-row) used during seeding, not the CSS grid size.
  const columns = 12;

  if (widgets.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{dashboard.title}</h2>
            {dashboard.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {dashboard.description}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={async () => {
              if (
                await confirm({
                  title: `Delete "${dashboard.title}"?`,
                  description: "This cannot be undone.",
                  destructive: true,
                })
              ) {
                deleteMutation.mutate();
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>
        </div>
        <EmptyState
          icon={BarChart3}
          message="No widgets yet. An agent will add visualizations during its next run."
        />
      </div>
    );
  }

  // Calculate max row for grid height
  const maxRow = Math.max(
    ...widgets.map((w: AgentDashboardWidget) => w.position.row + w.position.height),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{dashboard.title}</h2>
          {dashboard.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {dashboard.description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive"
          onClick={async () => {
            if (
              await confirm({
                title: `Delete "${dashboard.title}"?`,
                description: "This cannot be undone.",
                destructive: true,
              })
            ) {
              deleteMutation.mutate();
            }
          }}
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Delete
        </Button>
      </div>
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gridTemplateRows: `repeat(${maxRow}, 80px)`,
        }}
      >
        {widgets.map((widget: AgentDashboardWidget) => (
          <div
            key={widget.id}
            className="min-w-0 min-h-0"
            style={{
              gridColumn: `${widget.position.col + 1} / span ${widget.position.width}`,
              gridRow: `${widget.position.row + 1} / span ${widget.position.height}`,
            }}
          >
            <DashboardWidgetCard widget={widget} />
          </div>
        ))}
      </div>
    </div>
  );
}
